// Pages/inventario/components/ProductoCells.tsx
//
// Celdas de identidad de la tabla de productos (round 2026-08-06).
//
// Las dos resuelven el mismo problema: la tabla no dejaba IDENTIFICAR nada de
// un vistazo. El nombre venía suelto y sin recorte —desbordaba la columna—, y
// los tres tipos de ubicación se veían idénticos, con el único indicio de que
// las propiedades salían en mayúsculas.
//
// Se extraen a su propio archivo porque `inventario.tsx` ya pasa de 2.800
// líneas y porque estas celdas se reusan tal cual cuando la tabla pase a
// agrupar por producto (etapa 2 de la separación producto/existencia): ahí
// cambia la FILA, no cómo se dibuja un producto ni una ubicación.
import React from "react";
import { Image, Tag, Tooltip } from "antd";
import {
  HomeOutlined,
  InboxOutlined,
  PictureOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import type { Producto } from "../useInventarioLogic";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import "../inventario.css";

/**
 * Miniatura del producto. Cae a un ícono cuando no hay foto — un hueco vacío
 * desalinea la fila y se lee como "error de carga" en vez de "sin foto".
 */
export const ProductoMiniatura: React.FC<{ producto: Producto }> = ({
  producto,
}) => {
  if (!producto.miniatura) {
    return (
      <Tooltip title="Sin foto. Puedes subir una desde el botón de la cámara.">
        <div className="inventario-miniatura inventario-miniatura-vacia">
          <PictureOutlined />
        </div>
      </Tooltip>
    );
  }
  return (
    // Ampliable con un clic (round 2026-08-06). A 32px una foto sirve para
    // reconocer el producto pero no para MIRARLO, que es justo lo que se
    // necesita al decidir si dos filas parecidas son la misma cosa. El visor
    // usa la imagen COMPLETA, no el thumb ampliado: agrandar un WebP de 400px
    // se ve borroso exactamente cuando alguien quiere ver el detalle.
    <Image
      rootClassName="inventario-miniatura-wrap"
      className="inventario-miniatura inventario-miniatura-zoom"
      src={producto.miniatura}
      alt={producto.nombre}
      preview={{
        src: producto.miniaturaFull ?? producto.miniatura,
        mask: <span className="inventario-miniatura-mask">Ver</span>,
      }}
      // `loading="lazy"`: con 200 filas por página el navegador solo baja las
      // que se ven. Las URLs vienen firmadas y con TTL, así que igual conviene
      // no pedirlas todas de golpe.
      loading="lazy"
      // Una URL firmada vencida (o un archivo borrado del volumen) dejaría un
      // ícono de imagen rota. Se esconde y la fila queda limpia.
      onError={(e) => {
        (e.currentTarget as HTMLElement).style.visibility = "hidden";
      }}
    />
  );
};

/** Estilo por tipo de ubicación. El color es la señal; el ícono, el refuerzo. */
const ESTILO_UBICACION: Record<
  string,
  { color: string; icono: React.ReactNode; etiqueta: string }
> = {
  propiedades: {
    color: "purple",
    icono: <HomeOutlined />,
    etiqueta: "Propiedad",
  },
  bodegas: { color: "blue", icono: <ShopOutlined />, etiqueta: "Bodega" },
  bodeguitas: {
    color: "cyan",
    icono: <InboxOutlined />,
    etiqueta: "Bodeguita",
  },
  // Proveedor externo: de dónde salen las compras. No es un lugar del sistema
  // (no tiene tabla ni stock), y por eso se ve distinto a los tres que sí lo son.
  otros: {
    color: "gold",
    icono: <ShoppingCartOutlined />,
    etiqueta: "Compra externa",
  },
};

/**
 * Tag de una ubicación suelta. Existe para que el MISMO lugar se vea igual en
 * todas las pantallas: en la tabla de productos, en el recorrido de la bitácora
 * y en la línea de tiempo. Si cada vista eligiera su color, el admin tendría que
 * volver a aprender la clave en cada una.
 *
 * @param tipo   bodegas | bodeguitas | propiedades | otros
 * @param nombre nombre ya resuelto (snapshot histórico o actual)
 */
export const UbicacionTag: React.FC<{
  tipo: string | null | undefined;
  nombre: string | null | undefined;
}> = ({ tipo, nombre }) => {
  const estilo = ESTILO_UBICACION[String(tipo ?? "")] ?? ESTILO_UBICACION.otros;
  const texto =
    (tipo === "propiedades" && nombre ? formatPropertyName(nombre) : nombre) ||
    estilo.etiqueta;
  return (
    <Tooltip title={estilo.etiqueta}>
      <Tag color={estilo.color} icon={estilo.icono}>
        {texto}
      </Tag>
    </Tooltip>
  );
};

/**
 * Ubicación con su tipo explícito.
 *
 * `producto.ubicacion` viene ya armada por `formatUbicacion` e incluye la
 * geografía (comuna, sector) separada por " · ". Se muestra el PRIMER segmento
 * como nombre del lugar y el resto como contexto atenuado: en una tabla ancha,
 * lo que se busca con la vista es el lugar, no la región.
 */
export const UbicacionCell: React.FC<{ producto: Producto }> = ({
  producto,
}) => {
  const estilo = ESTILO_UBICACION[producto.ubicacionTipo];
  const partes = String(producto.ubicacion || "")
    .split(" · ")
    .map((x) => x.trim())
    .filter(Boolean);

  if (partes.length === 0) {
    // La ubicación se hidrata en dos fases (el nombre depende de listas
    // geográficas que cargan aparte). Mientras tanto se muestra el tipo, que sí
    // llega con la fila, en vez de una celda en blanco que parece un error.
    return estilo ? (
      <Tag color={estilo.color} icon={estilo.icono}>
        {estilo.etiqueta}
      </Tag>
    ) : (
      <span className="inventario-ubicacion-vacia">—</span>
    );
  }

  const [lugar, ...contexto] = partes;
  const nombreLugar =
    producto.ubicacionTipo === "propiedades" ? formatPropertyName(lugar) : lugar;

  return (
    <div className="inventario-ubicacion-cell">
      <Tooltip title={estilo?.etiqueta ?? "Ubicación"}>
        <Tag color={estilo?.color ?? "default"} icon={estilo?.icono}>
          {nombreLugar}
        </Tag>
      </Tooltip>
      {contexto.length > 0 && (
        <span className="inventario-ubicacion-contexto">
          {contexto.join(" · ")}
        </span>
      )}
    </div>
  );
};
